// src/pages/OfferPage.tsx
// Публичная страница «Публичная оферта»: полный текст оферты
// и отмеченный чекбокс принятия условий.
import React, { useState } from 'react';
import styled from 'styled-components';
import Footer from '../components/common/Footer';
import AddressText from '../components/common/AddressText';
import { useLang } from '../context/LangContext';

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background};
`;

const Main = styled.main`
  flex: 1;
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: 32px 24px;
`;

const Section = styled.section`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 20px 24px;
  margin-bottom: 16px;
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.6rem;
  margin: 0 0 4px;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 12px;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px 16px;
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 0.9rem;
  margin-bottom: 16px;
`;

const H2 = styled.h2`
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.1rem;
  margin: 0 0 10px;
`;

const P = styled.p`
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.5;
  margin: 0 0 8px;
  text-align: justify;
`;

const Details = styled.pre`
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.5;
  margin: 0 0 8px;
  font-family: inherit;
  white-space: pre-line;
`;

const ConsentRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.95rem;
  line-height: 1.45;
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  margin-top: 3px;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  accent-color: ${({ theme }) => theme.colors.primary};
`;

const OfferPage: React.FC = () => {
  const { t } = useLang();
  const [checked, setChecked] = useState(true);

  return (
    <Page>
      <Main>
        <Title>{t('offerTitle')}</Title>
        <Subtitle>{t('offerSubtitle')}</Subtitle>
        <MetaRow>
          <span>{t('offerMetaCity')}</span>
          <span>{t('offerMetaDate')}</span>
        </MetaRow>

        <Section>
          <P style={{ marginBottom: 0 }}>
            <AddressText text={t('offerIntro')} />
          </P>
        </Section>

        <Section>
          <H2>{t('offerS1Title')}</H2>
          <P><AddressText text={t('offerS1_1')} /></P>
          <P><AddressText text={t('offerS1_2')} /></P>
          <P><AddressText text={t('offerS1_3')} /></P>
          <P><AddressText text={t('offerS1_4')} /></P>
          <P style={{ marginBottom: 0 }}><AddressText text={t('offerS1_5')} /></P>
        </Section>

        <Section>
          <H2>{t('offerS2Title')}</H2>
          <P><AddressText text={t('offerS2_1')} /></P>
          <P><AddressText text={t('offerS2_2')} /></P>
          <P style={{ marginBottom: 0 }}><AddressText text={t('offerS2_3')} /></P>
        </Section>

        <Section>
          <H2>{t('offerS3Title')}</H2>
          <P><AddressText text={t('offerS3_1')} /></P>
          <P><AddressText text={t('offerS3_2')} /></P>
          <P><AddressText text={t('offerS3_3')} /></P>
          <P><AddressText text={t('offerS3_4')} /></P>
          <P style={{ marginBottom: 0 }}><AddressText text={t('offerS3_5')} /></P>
        </Section>

        <Section>
          <H2>{t('offerS4Title')}</H2>
          <P><AddressText text={t('offerS4_1')} /></P>
          <P><AddressText text={t('offerS4_2')} /></P>
          <P style={{ marginBottom: 0 }}><AddressText text={t('offerS4_3')} /></P>
        </Section>

        <Section>
          <H2>{t('offerS5Title')}</H2>
          <P><AddressText text={t('offerS5_1')} /></P>
          <P style={{ marginBottom: 0 }}><AddressText text={t('offerS5_2')} /></P>
        </Section>

        <Section>
          <H2>{t('offerS6Title')}</H2>
          <P><AddressText text={t('offerS6_1')} /></P>
          <P><AddressText text={t('offerS6_2')} /></P>
          <P style={{ marginBottom: 0 }}><AddressText text={t('offerS6_3')} /></P>
        </Section>

        <Section>
          <H2>{t('offerS7Title')}</H2>
          <P><AddressText text={t('offerS7_1')} /></P>
          <P><AddressText text={t('offerS7_2')} /></P>
          <P style={{ marginBottom: 0 }}><AddressText text={t('offerS7_3')} /></P>
        </Section>

        <Section>
          <H2>{t('offerS8Title')}</H2>
          <P style={{ marginBottom: 0 }}><AddressText text={t('offerS8_1')} /></P>
        </Section>

        <Section>
          <H2>{t('offerS9Title')}</H2>
          <P style={{ fontWeight: 600 }}><AddressText text={t('offerS9Name')} /></P>
          <Details>{t('offerS9Details')}</Details>
          <P style={{ marginTop: 8 }}>{t('offerS9ContactsTitle')}</P>
          <P><AddressText text={t('offerS9Phone')} /></P>
          <P><AddressText text={t('offerS9Email')} /></P>
          <P style={{ marginBottom: 0 }}><AddressText text={t('offerS9Site')} /></P>
        </Section>

        <Section>
          <ConsentRow>
            <Checkbox
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
            />
            <span>{t('offerConsentLabel')}</span>
          </ConsentRow>
        </Section>
      </Main>
      <Footer />
    </Page>
  );
};

export default OfferPage;